import React from "react";
import { Form } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationError } from "go-form/services/types";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { ApplicationApi } from "../../../../../../../../../services/Api/Organization/types";
import { AppState } from "../../../utils/types";
import { parseStringToList } from "../../../utils/utils";
import { GoOrderAcceptanceTimeSetting } from "./GoOrderAcceptanceTimeSetting";

interface Props {
	form: UseFormReturn<ApplicationApi>;
	appState: AppState;
	updateConfig: (prop: string, obj?: any) => void;
	updatedConfig: Record<string, any> | undefined;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<ApplicationApi>[]>>;
	setShowAdvancedSection: React.Dispatch<React.SetStateAction<boolean>>;
	showAdvancedSection: boolean;
}

const GoOrderAdvancedForm = ({
	form,
	appState,
	updatedConfig,
	updateConfig,
	setErrors,
	setShowAdvancedSection,
	showAdvancedSection,
}: Props) => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		watch,
		register,
		control,
	} = form;
	const defaultStores = parseStringToList(
		appState.app.settings,
		updatedConfig || {},
		"external_store_id",
		"stores"
	)?.map((store) => ({
		label: store?.name,
		id: store?.id,
	}));
	const defaultTerminals = parseStringToList(
		appState.app.settings,
		updatedConfig || {},
		"notification_terminal",
		"notification_terminals"
	)?.map((terminal) => ({
		label: terminal?.name,
		id: terminal?.id,
	}));
	const defaultCustomFields = parseStringToList(
		appState.app.settings,
		updatedConfig || {},
		"custom_fields_to_sync",
		"custom_fields"
	)?.map((cf) => ({
		label: cf?.label ?? cf?.name,
		value: cf?.slug,
	}));
	const acceptTypes = [
		{ value: "MANUAL", label: t("enums.apps.goorder.accept_type.DEFAULT") },
		{ value: "AUTO_ACCEPT", label: t("enums.apps.goorder.accept_type.AUTO_ACCEPT") },
		{ value: "AUTO_CLOSE", label: t("enums.apps.goorder.accept_type.AUTO_CLOSE") },
	];
	const settings = watch("settings");

	const searchStores = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getGoOrderStoresSearchSelect(appState.app.id, params, search, {
			cancelToken: options?.token,
		});
	};

	const searchTerminals = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getTerminalsSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const searchCustomFields = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newParams = {
			...params,
			"resource_type|e": "ORDER",
		};
		return apiOrganization.getCustomFieldsSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	const searchImageTags = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newParams = {
			...params,
			"resource_type|e": "MENU_IMAGE",
		};
		return apiOrganization.getTagsSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	const searchPointOfSales = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getPointsOfSaleSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	return (
		<>
			<div className="my-3">
				<div className="d-flex align-items-center gap-1 mb-2">
					<Form.Check
						type="switch"
						id="show"
						checked={showAdvancedSection}
						onClick={() => setShowAdvancedSection((prevState) => !prevState)}
					/>
					<h6 className="mb-0">{t("common.word.advanced", { ns: "lib" })}</h6>
				</div>
				{showAdvancedSection ? (
					<>
						{appState.config?.stores && (
							<FormSelectGroup
								isMulti
								name="settings.external_store_id"
								onChange={(_, fullObj) => updateConfig("stores", fullObj)}
								getOptionValue={(option) => option.id}
								label={t("modules.app.field.linked_stores.title")}
								errors={errors}
								defaultValue={defaultStores}
								loadOptions={searchStores}
								control={control}
								data-testid="settings.external_store_id"
							/>
						)}
						<FormSelectGroup
							isMulti
							name="settings.notification_terminal"
							getOptionValue={(option) => option.id}
							onChange={(_, fullObj) => updateConfig("notification_terminals", fullObj)}
							label={t("modules.app.field.default_terminals.title")}
							errors={errors}
							defaultValue={defaultTerminals}
							loadOptions={searchTerminals}
							control={control}
							data-testid="settings.notification_terminal"
						/>
						<FormSelectGroup
							label={t("modules.app.field.accept_type.title")}
							name="settings.order_accept_type"
							placeholder={t("enums.apps.goorder.accept_type.DEFAULT")}
							defaultValue={acceptTypes.find(
								(acceptType) => acceptType.value === appState.app.settings.order_accept_type
							)}
							errors={errors}
							options={acceptTypes}
							control={control}
							data-testid="settings.order_accept_type"
						/>
						<FormSelectGroup
							name="settings.image_tag"
							getOptionLabel={(option) => option.label}
							getOptionValue={(option) => option.label}
							defaultValue={{ id: settings.image_tag, label: settings.image_tag }}
							label={t("modules.app.field.image_tag.title")}
							help={t("modules.app.field.image_tag.helptext.description")}
							errors={errors}
							loadOptions={searchImageTags}
							control={control}
							data-testid="settings.image_tag"
						/>
						<FormSelectGroup
							isMulti
							name="settings.custom_fields_to_sync"
							getOptionLabel={(option) => option.label}
							getOptionValue={(option) => option.slug}
							label={t("modules.app.action.custom_fields_sync.title")}
							errors={errors}
							defaultValue={defaultCustomFields}
							loadOptions={searchCustomFields}
							control={control}
							data-testid="settings.custom_fields_to_sync"
						/>
						<GoOrderAcceptanceTimeSetting form={form} setErrors={setErrors} />
						<FormCheck
							type="switch"
							register={register}
							label={t("modules.app.action.auto_link_by_name.title")}
							name="settings.auto_link_by_name"
							errors={errors}
						/>
						{appState.showPointsOfSale && (
							<FormSelectGroup
								name="settings.point_of_sale_id"
								getOptionValue={(option) => option.id}
								label={t("common.word.point_of_sale")}
								errors={errors}
								defaultValue={{
									label: appState.config?.point_of_sale_name,
									id: appState.app.settings.point_of_sale_id,
								}}
								loadOptions={searchPointOfSales}
								control={control}
								data-testid="settings.point_of_sale_id"
							/>
						)}
						<FormCheck
							type="switch"
							register={register}
							label={t("modules.app.field.external_delivery.title")}
							name="settings.external_delivery"
							errors={errors}
						/>
						<FormCheck
							type="switch"
							register={register}
							label={t("modules.app.field.item_multi_level.title")}
							name="settings.item_multi_level"
							errors={errors}
						/>
					</>
				) : (
					<></>
				)}
			</div>
			<hr />
		</>
	);
};

export default GoOrderAdvancedForm;
